import React from 'react';
import { LoadingData } from '../../../../ui/LoadingData';
import { NotFoundData } from '../../../../ui/NotFoundData';
import { LoadError } from '../../../../shared/components/core-ui/load-error/load-error.component';
import { CHIP } from '../../../shared/lead-visuals';

/**
 * One dashboard section: heading, and its own loading, error and empty states.
 *
 * Each section owns its request, so a slow or broken aggregation shows an
 * error inside its own card instead of blanking the page. Nine sections behind
 * one request would make the dashboard only as available as its worst query.
 */
export const OpsSection: React.FC<{
  title: string;
  subtitle?: string;
  accent?: string;
  loading: boolean;
  error?: string;
  empty?: boolean;
  onRetry?: () => void;
  children: React.ReactNode;
}> = ({ title, subtitle, accent = 'bg-primary-400', loading, error, empty, onRetry, children }) => (
  <section className="rounded-2xl border border-creams-600 bg-white p-5 shadow-md">
    <div className="mb-3">
      <h2 className="flex items-center gap-2 font-gilroy-bold text-base text-neutral-500">
        <span className={`h-4 w-1 rounded-full ${accent}`} aria-hidden="true" />
        {title}
      </h2>
      {subtitle ? <p className="mt-1 text-xs text-neutral-400">{subtitle}</p> : null}
    </div>
    {loading ? (
      <LoadingData />
    ) : error ? (
      <LoadError message={error} onRetry={onRetry} />
    ) : empty ? (
      <NotFoundData />
    ) : (
      children
    )}
  </section>
);

/** A headline number. The thing someone reads first and quotes later. */
export const OpsStat: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  tone?: 'default' | 'good' | 'bad';
}> = ({ label, value, hint, tone = 'default' }) => {
  const toneClass =
    tone === 'good'
      ? 'text-primary-600'
      : tone === 'bad'
      ? 'text-secondary-600'
      : 'text-neutral-500';
  return (
    <div className="rounded-xl bg-creams-100 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-neutral-300">{label}</div>
      <div className={`font-gilroy-bold text-2xl ${toneClass}`}>{value}</div>
      {hint ? <div className="mt-0.5 text-xs text-neutral-400">{hint}</div> : null}
    </div>
  );
};

/**
 * A horizontal bar list — the right shape for a breakdown with a long tail.
 *
 * A pie chart of twelve channels is unreadable; a sorted bar list stays
 * readable at any length and puts the labels where they can be read.
 */
export const OpsBars: React.FC<{
  rows: { key: string; count: number }[];
  label?: (key: string) => string;
  tone?: (key: string) => string;
  emptyText?: string;
}> = ({ rows, label, tone, emptyText = 'Nothing in this window' }) => {
  if (!rows.length) return <p className="text-sm text-neutral-400">{emptyText}</p>;
  const max = Math.max(...rows.map((row) => row.count), 1);
  const total = rows.reduce((sum, row) => sum + row.count, 0);

  return (
    <div className="flex flex-col gap-2">
      {rows.map((row) => (
        <div key={row.key} className="flex items-center gap-3">
          <span className="w-40 shrink-0 truncate text-sm text-neutral-500" title={row.key}>
            {label ? label(row.key) : row.key}
          </span>
          <span className="h-2 flex-1 overflow-hidden rounded-full bg-creams-300">
            <span
              className={`block h-full rounded-full ${tone ? tone(row.key) : 'bg-primary-400'}`}
              style={{ width: `${(row.count / max) * 100}%` }}
            />
          </span>
          <span className="w-24 shrink-0 text-right text-sm text-neutral-400">
            {row.count}
            <span className="ml-1 text-xs text-neutral-300">
              {total ? `${Math.round((row.count / total) * 100)}%` : ''}
            </span>
          </span>
        </div>
      ))}
    </div>
  );
};

export const OpsChip: React.FC<{ className: string; children: React.ReactNode }> = ({
  className,
  children
}) => <span className={`${CHIP} ${className}`}>{children}</span>;

export const TH = 'whitespace-nowrap px-3 py-2 font-gilroy-bold text-neutral-500 text-left';
export const TD = 'border-t border-creams-500 px-3 py-2 whitespace-nowrap';
